import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TrainingService } from '@/services/training.service';
import { TrainingModuleViewer } from '@/components/training/TrainingModuleViewer';
import { useAuth } from '@/contexts/AuthContext';
import { OperationsRole } from '@/models/user.model';
import { Button, Card, Badge, LoadingSpinner, EmptyState } from '@/components/common';
import { BookOpen, ArrowLeft, Edit, Play, Clock, FileText, Video, Tag, CheckCircle2 } from 'lucide-react';

export const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [viewMode, setViewMode] = useState<'details' | 'training'>('details');

  const { data: course, isLoading, error } = useQuery({
    queryKey: ['trainingCourse', id],
    queryFn: () => (id ? TrainingService.getCourseById(id) : null),
    enabled: Boolean(id),
  });

  // Map user role to training level
  const getUserTrainingLevel = (): 'executive' | 'management' | 'staff' => {
    if (!profile?.role) return 'staff';
    
    const role = profile.role;
    if (role === OperationsRole.executive || role === OperationsRole.operationsAdmin) {
      return 'executive';
    }
    if (
      role === OperationsRole.salesManager ||
      role === OperationsRole.hrManager
    ) {
      return 'management';
    }
    return 'staff';
  };

  if (isLoading) return <LoadingSpinner text="Loading course..." fullScreen />;

  if (error || !course) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={BookOpen}
          title={error ? "Failed to load course" : "Course not found"}
          description={error instanceof Error ? error.message : "The requested course does not exist or you may not have access."}
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/training')}>
              Back to Training
            </Button>
          }
        />
      </div>
    );
  }

  // Show training viewer if in training mode
  if (viewMode === 'training' && course) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <TrainingModuleViewer
          course={course}
          level={getUserTrainingLevel()}
          onComplete={async (score, passed) => {
            // TODO: Update assignment status when course is completed
            console.log('Course completed:', { score, passed });
            setViewMode('details');
            // Optionally navigate or show success message
          }}
          onBack={() => setViewMode('details')}
        />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/training')}
          className="mb-4"
        >
          Back to Training
        </Button>
        <Card>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h1 className="text-3xl font-bold text-gray-900">{course.title}</h1>
                  {course.isRequired && (
                    <Badge variant="danger" size="sm">Required</Badge>
                  )}
                  {!course.isActive && (
                    <Badge variant="gray" size="sm">Inactive</Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                  <span>{course.type}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {course.duration} minutes
                  </span>
                  {course.category && <span>{course.category}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="success"
                size="sm"
                icon={Play}
                onClick={() => setViewMode('training')}
              >
                Start Training
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon={Edit}
                onClick={() => navigate(`/training/courses/${course.id}/edit`)}
              >
                Edit
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Card className="space-y-6">
        {course.description && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Description
            </h2>
            <p className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{course.description}</p>
          </div>
        )}

        {course.tags?.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Tag className="w-5 h-5 text-blue-600" />
              Tags
            </h2>
            <div className="flex flex-wrap gap-2">
              {course.tags.map((tag) => (
                <Badge key={tag} variant="info" size="md">{tag}</Badge>
              ))}
            </div>
          </div>
        )}

        {(course.videoUrl || course.documentUrl) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {course.videoUrl && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Video className="w-5 h-5 text-blue-600" />
                  Video
                </h2>
                <a
                  href={course.videoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 break-all"
                >
                  {course.videoUrl}
                </a>
              </div>
            )}
            {course.documentUrl && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Document
                </h2>
                <a
                  href={course.documentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 break-all"
                >
                  {course.documentUrl}
                </a>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {course.passingScore !== undefined && course.passingScore !== null && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
                Passing Score
              </h2>
              <p className="text-2xl font-bold text-gray-900">{course.passingScore}%</p>
            </div>
          )}

          {course.quiz && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Quiz
              </h2>
              <p className="text-2xl font-bold text-gray-900">
                {course.quiz.questions.length} question{course.quiz.questions.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>

        <div className="pt-6 border-t">
          <Button
            variant="success"
            fullWidth
            size="lg"
            icon={Play}
            onClick={() => setViewMode('training')}
          >
            Start Training Course
          </Button>
        </div>
      </Card>
    </div>
  );
};

